import styled from '@emotion/styled';
import Logo from './Logo';
import { navLinks, bookDemoLink, loginLink } from './nav';
import { colors, fonts, radii } from './theme';

const HeaderRoot = styled.header`
  position: sticky;
  top: 0;
  z-index: 20;
  background: ${colors.coreBlue};
  min-height: 74px;
  display: flex;
  align-items: center;
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 14px clamp(20px, 4vw, 32px);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Nav = styled.nav`
  display: none;
  align-items: center;
  gap: 32px;

  @media (min-width: 900px) {
    display: flex;
  }
`;

const NavLink = styled.a`
  font-family: ${fonts.sans};
  font-size: 16px;
  color: ${colors.signalBlue};
  text-decoration: none;

  &:hover {
    color: ${colors.baselineWhite};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LoginButton = styled.a`
  box-sizing: border-box;
  font-family: ${fonts.sans};
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  letter-spacing: -0.01em;
  color: ${colors.baselineWhite};
  padding: 9px 16px;
  height: 40px;
  display: none;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.borderInverse};
  border-radius: ${radii.sm};
  text-decoration: none;
  white-space: nowrap;

  @media (min-width: 900px) {
    display: inline-flex;
  }

  &:hover {
    border-color: ${colors.signalBlue};
    color: ${colors.signalBlue};
  }
`;

const BookDemoButton = styled.a`
  box-sizing: border-box;
  font-family: ${fonts.sans};
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  letter-spacing: -0.01em;
  color: ${colors.actionAccentFg};
  background: ${colors.actionAccentBg};
  padding: 9px 16px;
  height: 40px;
  gap: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: ${radii.sm};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: ${colors.actionAccentBgHover};
  }
`;

const MenuButton = styled.button`
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;

  @media (min-width: 900px) {
    display: none;
  }

  span {
    display: block;
    width: 22px;
    height: 2px;
    background: ${colors.baselineWhite};
    margin: 3px 0;
  }
`;

interface HeaderProps {
  onMenuClick: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  return (
    <HeaderRoot>
      <HeaderInner>
        <Logo href="/" height={26} />
        <Nav>
          {navLinks.map((link) => (
            <NavLink key={link.href} href={link.href}>
              {link.label}
            </NavLink>
          ))}
        </Nav>
        <Actions>
          <LoginButton href={loginLink.href}>{loginLink.label}</LoginButton>
          <BookDemoButton href={bookDemoLink.href}>{bookDemoLink.label}</BookDemoButton>
          <MenuButton aria-label="Open menu" onClick={onMenuClick}>
            <span />
            <span />
            <span />
          </MenuButton>
        </Actions>
      </HeaderInner>
    </HeaderRoot>
  );
}
